import clsx from 'clsx';
import { useTranslate } from 'core/hooks/useTranslate';
import PropTypes from 'prop-types';
import React from 'react';
import Icon from './../Icon';

const InputText = React.forwardRef(
  (
    {
      background,
      disabled,
      error,
      icon,
      iconColor,
      iconLeft,
      iconSize,
      label,
      onChange,
      onClickIcon,
      onKeyPress,
      placeholder,
      referer,
      type,
      variant,
      className,
      iconAriaLabel,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslate();
    const refInput = referer ? referer : ref;

    const getIconAriaLabel = (icon) => {
      const iconLabels = {
        visible: t('buttons:Show password'),
        'visible-off': t('buttons:Hide password'),
        search: t('buttons:Search'),
        clear: t('buttons:Clear field'),
        calendar: t('buttons:Open calendar'),
        eye: t('buttons:Show password'),
        'eye-off': t('buttons:Hide password'),
      };

      return iconLabels[icon] || t('buttons:Click {{icon}} icon', { icon });
    };

    const isIconClickable = onClickIcon !== InputText.defaultProps.onClickIcon && typeof onClickIcon === 'function';

    return (
      <div
        className={clsx(
          'input-text',
          `input-text--${variant}`,
          className,
          `input-text--background-${background}`,
          { 'input-text--icon': !!icon },
          { 'input-text--icon-click': isIconClickable },
          { 'input-text--icon-left': iconLeft },
          { 'input-text--error': !!error }
        )}
      >
        {label && (
          <label className="input-text__label">
            {label} {props.required && <sup>*</sup>}
          </label>
        )}

        <div className="input-text__input-container">
          <input
            className={clsx('input-text__input', { 'input-text__input--disabled': disabled })}
            type={type}
            placeholder={placeholder}
            onChange={onChange}
            disabled={disabled}
            ref={refInput}
            onKeyPress={onKeyPress}
            {...props}
          />
          {icon && (
            <div
              className="input-text__icon"
              onClick={isIconClickable ? onClickIcon : undefined}
              onKeyDown={
                isIconClickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClickIcon();
                      }
                    }
                  : undefined
              }
              role={isIconClickable ? 'button' : undefined}
              tabIndex={isIconClickable ? 0 : undefined}
              aria-label={isIconClickable ? iconAriaLabel || getIconAriaLabel(icon) : undefined}
            >
              <Icon type={icon} size={iconSize} color={iconColor} />
            </div>
          )}
        </div>
        {error && <span className="input-text__error">{error}</span>}
      </div>
    );
  }
);

InputText.propTypes = {
  background: PropTypes.oneOf(['default', 'grey']),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  icon: PropTypes.string,
  iconAriaLabel: PropTypes.string,
  iconColor: PropTypes.string,
  iconLeft: PropTypes.bool,
  iconSize: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  onClickIcon: PropTypes.func,
  onKeyPress: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  referer: PropTypes.object,
  required: PropTypes.bool,
  type: PropTypes.string,
  value: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'focus']),
};

InputText.defaultProps = {
  background: 'default',
  disabled: false,
  iconLeft: false,
  onChange: () => null,
  onClickIcon: () => null,
  onKeyPress: () => {},
  placeholder: 'Write here',
  type: 'text',
  variant: 'default',
  iconSize: 'big',
};

InputText.displayName = 'InputText';

export default InputText;
